import { useMemo, useState, type ReactElement } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  useWorkoutsInRange,
  type PlannedWorkout,
  type WorkoutsResponse,
} from '@/lib/workouts';
import {
  useRescheduleWeek,
  type RescheduleReason,
} from '@/lib/coach';
import { useJobs } from '@/lib/jobs';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';
import {
  addDaysUTC,
  dayKeyUTC,
  startOfWeekISO,
} from './dateUtils';

/**
 * M2.32.C — Botón "🔄 Reajustar semana" para vista Día/Semana.
 *
 * Comportamiento:
 *  1. Lee los workouts de la semana visible vía `useWorkoutsInRange`.
 *  2. Si no hay planneds → deshabilitado + tooltip "Nada que reajustar".
 *  3. Si los planneds existen pero todos son completed → mismo deshabilitado.
 *  4. Click → abre `ConfirmDialog` con el conteo de skipped/missed/pending.
 *  5. Confirm → dispara mutation + `startJob('coach-plan')` para que
 *     el banner global muestre el progreso aunque el usuario navegue.
 *
 * El job se registra con `resultPath` apuntando a la semana actual para
 * que al terminar el botón "Ver →" lleve al usuario al rango correcto.
 */
export function RescheduleWeekButton({
  cursor,
}: {
  cursor: Date;
}): ReactElement {
  const { fromISO, toISO, weekStartDate } = useMemo(() => {
    const start = startOfWeekISO(cursor);
    const end = addDaysUTC(start, 6);
    return {
      fromISO: `${dayKeyUTC(start)}T00:00:00.000Z`,
      toISO: `${dayKeyUTC(end)}T23:59:59.999Z`,
      weekStartDate: start,
    };
  }, [cursor]);

  const weekStartISO = dayKeyUTC(weekStartDate);

  const workoutsQuery = useWorkoutsInRange(fromISO, toISO);
  const reschedule = useRescheduleWeek();
  const jobs = useJobs();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const counts = useMemo(
    () => countByStatus(workoutsQuery.data ?? null),
    [workoutsQuery.data],
  );

  const hasAnythingToReschedule =
    counts.pending + counts.missed > 0;
  const disabled = !workoutsQuery.data || !hasAnythingToReschedule;

  const tooltip = (() => {
    if (workoutsQuery.isLoading) return 'Cargando semana…';
    if (!workoutsQuery.data) return 'No se pudo leer la semana';
    if (!hasAnythingToReschedule) return 'Nada que reajustar';
    return 'Pedir al coach que redistribuya el resto de la semana';
  })();

  function handleConfirm(): void {
    setServerError(null);
    const reason: RescheduleReason =
      counts.skipped > 0
        ? 'skipped'
        : counts.partial > 0
          ? 'partial'
          : 'auto';
    const promise = reschedule.mutateAsync({
      weekStartISO,
      reason,
    });
    // Registramos el job para que JobsBanner pinte el progreso aunque el
    // usuario navegue durante los 10-30s que tarda Claude.
    jobs.startJob({
      type: 'coach-plan',
      label: `Reajustando semana del ${weekStartISO}`,
      promise,
      resultPath: `/entrenos?view=week&week=${weekStartISO}`,
    });
    // Cerramos el dialog inmediatamente — el banner se hace cargo del feedback.
    setDialogOpen(false);
    promise.catch((err: unknown) => {
      if (err instanceof Error) {
        setServerError(err.message);
      } else {
        setServerError('Error desconocido al reajustar la semana');
      }
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        disabled={disabled || reschedule.isPending}
        title={tooltip}
        aria-label="Reajustar resto de la semana"
        data-testid="calendar-reschedule"
        className="inline-flex items-center gap-1 rounded border border-amber-600 bg-amber-600/10 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-600/15 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        Reajustar semana
      </button>

      <ConfirmDialog
        open={dialogOpen}
        title="¿Reajustar el resto de la semana?"
        description={
          <div className="space-y-2">
            <p>
              Detectados{' '}
              <strong data-testid="reschedule-count-skipped">
                {counts.skipped}
              </strong>{' '}
              entreno(s) saltado(s),{' '}
              <strong data-testid="reschedule-count-missed">
                {counts.missed}
              </strong>{' '}
              perdido(s) sin marcar y{' '}
              <strong data-testid="reschedule-count-pending">
                {counts.pending}
              </strong>{' '}
              pendiente(s) por hacer.
            </p>
            <p>
              El coach IA redistribuirá las sesiones pendientes y perdidas
              para que el TSS objetivo se mantenga lo más cerca posible.
              <br />
              Las completed/partial/skipped <strong>NO se tocarán</strong>.
            </p>
            <p className="text-xs text-slate-500">
              Tarda entre 10 y 30 segundos. Puedes seguir navegando.
            </p>
            {serverError && (
              <p
                role="alert"
                className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700"
              >
                {serverError}
              </p>
            )}
          </div>
        }
        confirmLabel="Reajustar"
        cancelLabel="Cancelar"
        isPending={reschedule.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setDialogOpen(false)}
      />
    </>
  );
}

interface StatusCounts {
  completed: number;
  partial: number;
  skipped: number;
  pending: number;
  /** Planneds con datetime <= now y status='planned' (no marcados). */
  missed: number;
}

/**
 * Cuenta los planneds por estado en el conjunto cargado. El backend hace su
 * propia clasificación canónica; aquí solo necesitamos los conteos para el
 * texto del dialog (estimación del lado cliente, suficientemente buena).
 */
function countByStatus(workouts: WorkoutsResponse | null): StatusCounts {
  const counts: StatusCounts = {
    completed: 0,
    partial: 0,
    skipped: 0,
    pending: 0,
    missed: 0,
  };
  if (!workouts) return counts;
  const nowMs = Date.now();
  for (const p of workouts.planned) {
    switch (p.status) {
      case 'completed':
        counts.completed += 1;
        break;
      case 'partial':
        counts.partial += 1;
        break;
      case 'skipped':
        counts.skipped += 1;
        break;
      case 'planned':
      default: {
        const dt = Date.parse(p.datetime);
        if (Number.isFinite(dt) && dt > nowMs) counts.pending += 1;
        else counts.missed += 1;
        break;
      }
    }
  }
  return counts;
}

/** Re-export para tests que necesiten el helper sin importar todo el módulo. */
export { countByStatus as _countByStatusForTests };
export type { StatusCounts as _StatusCountsForTests, PlannedWorkout };
